import { Col, Row } from "antd";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { GENDER, MILTIS_SOCIAL_REGIMES } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

const NUMBERS_MAPPING = {
  1: "1er",
  2: "2ème",
  3: "3ème",
  4: "4ème",
  5: "5ème",
  6: "6ème",
  7: "7ème",
  8: "8ème",
  9: "9ème",
  10: "10ème",
  11: "11ème",
  12: "12ème",
};

function View({ children, spouse, onNext, onBack, territorial = false }) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: { children, spouse },
    onSubmit: onNext,
    key: JSON.stringify(children, spouse),
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      { hasPartner: !isEmpty(spouse) },
    ),
  });

  return (
    <Layout>
      <Header
        title={`Nous avons besoin <span style="color: #4AA1F5">d'informations complémentaires</span> concernant <span style="color: #4AA1F5">vos bénéficiaires</span>`}
        subtitle="Vos bénéficiaires"
        onBack={onBack}
      />
      <div className="miltis-related-persons">
        <div className="miltis-related-persons__wrapper">
          {!isEmpty(spouse) && (
            <>
              <div className="miltis-related-persons__section">
                <h3 className="miltis-related-persons__section-title">
                  Informations de votre conjoint
                </h3>
                <div className="miltis-form">
                  <div className="miltis-form__container">
                    <Input
                      row
                      size="full"
                      label={"Civilité"}
                      type="radio"
                      values={[
                        {
                          value: GENDER.MALE,
                          label: "Homme",
                        },
                        {
                          value: GENDER.FEMALE,
                          label: "Femme",
                        },
                      ]}
                      {...bindInputProps({
                        name: "spouse.identity.user.gender",
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue("spouse.identity.user.gender", value)
                      }
                    />
                    <div className="miltis-form__row">
                      <Input
                        placeholder={"ex : Doe"}
                        label={"Nom"}
                        {...bindInputProps({
                          name: "spouse.identity.user.lastname",
                          values,
                          ...formProps,
                        })}
                      />

                      <Input
                        placeholder={"ex : John"}
                        label={"Prénom"}
                        {...bindInputProps({
                          name: "spouse.identity.user.firstname",
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                    <div className="miltis-form__row">
                      <Input
                        placeholder={"ex : 185057800608436"}
                        label={"N° de Sécurité sociale"}
                        trim={true}
                        {...bindInputProps({
                          name: "spouse.identity.socialSecurityNumber",
                          values,
                          ...formProps,
                        })}
                      />

                      <Select
                        options={Object.values(MILTIS_SOCIAL_REGIMES)
                          .filter(
                            (option) =>
                              !territorial ||
                              option !==
                                MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                          )
                          .map((option) => {
                            const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                              (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                            );
                            return {
                              value: option,
                              label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                            };
                          })}
                        placeholder="Sélectionnez un régime"
                        label="Régime social"
                        value={get(values, "spouse.identity.socialRegime")}
                        onChange={(value) =>
                          setFieldValue("spouse.identity.socialRegime", value)
                        }
                        errors={get(
                          formProps.errors,
                          "spouse.identity.socialRegime",
                          "",
                        )}
                        touched={get(
                          formProps.touched,
                          "spouse.identity.socialRegime",
                          false,
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="miltis-related-persons__divider" />
            </>
          )}
          {children.map((_, i) => {
            return (
              <div
                key={`child_${i}`}
                className="miltis-related-persons__section"
              >
                <h3 className="miltis-related-persons__section-title">
                  Informations de votre {NUMBERS_MAPPING[i + 1]} enfant
                </h3>
                <div className="miltis-form">
                  <div className="miltis-form__container">
                    <Input
                      row
                      size="full"
                      label={"Civilité"}
                      type="radio"
                      values={[
                        {
                          value: GENDER.MALE,
                          label: "Homme",
                        },
                        {
                          value: GENDER.FEMALE,
                          label: "Femme",
                        },
                      ]}
                      {...bindInputProps({
                        name: `children.${i}.identity.user.gender`,
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue(
                          `children.${i}.identity.user.gender`,
                          value,
                        )
                      }
                    />
                  </div>
                  <div className="miltis-form__row">
                    <Input
                      placeholder={"ex : Doe"}
                      label={"Nom"}
                      {...bindInputProps({
                        name: `children.${i}.identity.user.lastname`,
                        values,
                        ...formProps,
                      })}
                    />

                    <Input
                      placeholder={"ex : John"}
                      label={"Prénom"}
                      {...bindInputProps({
                        name: `children.${i}.identity.user.firstname`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      placeholder={"ex : 185057800608436"}
                      label={"N° de Sécurité sociale"}
                      trim={true}
                      {...bindInputProps({
                        name: `children.${i}.identity.socialSecurityNumber`,
                        values,
                        ...formProps,
                      })}
                    />

                    <Select
                      options={Object.values(MILTIS_SOCIAL_REGIMES)
                        .filter(
                          (option) =>
                            !territorial ||
                            option !==
                              MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                        )
                        .map((option) => {
                          const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                            (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                          );
                          return {
                            value: option,
                            label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                          };
                        })}
                      placeholder="Sélectionnez un régime"
                      label="Régime social"
                      value={get(values, `children.${i}.identity.socialRegime`)}
                      onChange={(value) =>
                        setFieldValue(
                          `children.${i}.identity.socialRegime`,
                          value,
                        )
                      }
                      errors={get(
                        formProps.errors,
                        `children.${i}.identity.socialRegime`,
                        "",
                      )}
                      touched={get(
                        formProps.touched,
                        `children.${i}.identity.socialRegime`,
                        false,
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <ActionButtons
            disabled={!isEmpty(formProps.errors)}
            onNext={handleSubmit}
            onBack={onBack}
          />
          <DataProtection />
        </div>
      </div>
    </Layout>
  );
}

export default View;
